/**
 * Action: Answer (respond to a complaint). Statewide base with the court layer.
 *
 * Its checklist is the steps in answer.steps.json, each resting on rules in rules/ (see lib/rulebook.js). This module
 * keeps the questions, the facts the steps' conditions share, the values their text fills in, and the flag and rows
 * at the top of the checklist. Court specifics come from src/courts/ through the steps' `court` details; anything the
 * files do not cover is listed under "verify" rather than asserted.
 */
import { CLASS_OPTS, CLASS_TXT, firstPaperFee, fmt, fmtISO, isLimited, money, parse, todayISO } from "../lib/civil.js";
import { buildChecklist, optsWhen, when } from "../lib/rulebook.js";
import STEPS from "./answer.steps.json" with { type: "json" };

export const id = "answer";
export const statewide = true;
// The statewide forms its checklist can link, which a case offers when this action is added to it: the forms its steps link.
export const forms = ["ca-pld-050", "ca-pld-pi-003", "ca-pld-c-010", "ca-ud-105", "ca-cm-010", "ca-pos-040", "ca-pos-050", "ca-disc-010", "ca-cm-110"];

const yesNo = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }];
const KIND_TXT = {
  pi: "Personal injury, property damage or wrongful death",
  contract: "Contract",
  other: "Another kind of claim",
};
const FORM_ANSWER = { pi: "PLD-PI-003", contract: "PLD-C-010", ud: "UD-105" };

// The conditions the answer's steps share.
const facts = {
  ud: "ctype = ud",
  limited: "cls in (limitedOver10k, limitedUpTo10k)",
  // The economic litigation rules for limited civil cases (CCP 90 et seq.) don't apply to an unlawful detainer (CCP 91(b)).
  economicLitigation: "limited and not ud",
  // A general denial is enough if the complaint is unverified or the case is under the economic litigation rules (CCP 431.30(d)).
  generalDenialOK: "verified = no or economicLitigation",
  formAvailable: "ud or kind in (pi, contract)",
};
const isUD = (a) => a.ctype === "ud";
const formFor = (a) => (isUD(a) ? FORM_ANSWER.ud : FORM_ANSWER[a.kind]);

// What kind of case it is, kept once per case (caseFacts in src/cases.js). A collections case isn't an unlawful detainer.
export const caseFacts = {
  from: (f) => ({
    ctype: f.ud === "yes" ? "ud" : f.ud === "no" || f.collections === "yes" ? "general" : undefined,
    cls: f.cls, kind: f.kind,
  }),
  to: (a) => ({
    ud: a.ctype ? (isUD(a) ? "yes" : "no") : undefined,
    cls: a.cls, limited: a.cls ? (isLimited(a.cls) ? "yes" : "no") : undefined,
    kind: a.ctype === "general" ? a.kind : undefined,
  }),
};

/* ------------------------------------------------------------------ steps */
export const steps = [
  {
    id: "case",
    title: "The case",
    intro: "These answers set the fee, the form the answer can take and the deadline. The wizard doesn't ask for names or case details.",
    fields: [
      {
        k: "ctype", type: "radio", label: "What kind of case is it?", req: true,
        opts: [{ v: "general", l: "A civil case other than an unlawful detainer" }, { v: "ud", l: "An unlawful detainer" }],
      },
      { k: "cls", type: "radio", label: "How is the case classified?", req: true, opts: CLASS_OPTS },
      {
        k: "kind", type: "radio", label: "What kind of claim is it?", req: true,
        show: when("ctype = general", facts),
        opts: Object.entries(KIND_TXT).map(([v, l]) => ({ v, l })),
      },
      { k: "verified", type: "radio", label: "Is the complaint verified?", req: true, hint: "Signed under oath or penalty of perjury by the plaintiff or its attorney.", opts: yesNo },
      {
        k: "complex", type: "radio", label: "Has the case been designated complex, or will the defendant designate it complex?", req: true,
        show: when("ctype = general and cls = unlimited", facts),
        opts: yesNo,
      },
    ],
  },
  {
    id: "form",
    title: "The answer",
    intro: "Which forms the answer can use depends on your earlier answers.",
    fields: [
      {
        k: "pleading", type: "radio", label: "How will the answer be prepared?", req: true,
        opts: (() => {
          const o = optsWhen([
            { v: "general", l: "General denial (PLD-050)", when: "generalDenialOK" },
            { v: "form", l: "On the Judicial Council answer form", when: "formAvailable" },
            { v: "paper", l: "On pleading paper" },
          ], facts);
          const f = (a) => o(a).map((x) => (x.v === "form" ? { ...x, l: `${x.l} (${formFor(a)})` } : x));
          f.all = o.all;
          return f;
        })(),
      },
      {
        k: "xc", type: "radio", label: "Will the defendant also file a cross-complaint against the plaintiff?", req: true,
        show: when("ctype = general", facts),
        opts: yesNo,
      },
    ],
  },
  {
    id: "timing",
    title: "Service and timing",
    intro: (a, court) => `The due date is counted on the calendar of ${court.name}.`,
    fields: [
      {
        k: "served", type: "date", req: true, label: "Date service of the summons and complaint was complete",
        hint: "Personal delivery: that day (CCP 415.10). Substituted service, or mail to a defendant outside California: the 10th day after mailing (CCP 415.20, 415.40). Notice and acknowledgment: the day the acknowledgment was signed (CCP 415.30(c)).",
      },
      {
        k: "ext", type: "radio", label: "Has the time to respond been extended?", req: true,
        show: when("ctype = general", facts),
        opts: [
          { v: "none", l: "No" },
          { v: "stip", l: "Yes, the parties agreed to the one 15-day extension" },
          { v: "order", l: "Yes, by court order" },
        ],
      },
      { k: "extDate", type: "date", req: true, label: "New due date in the order", show: when("ext = order", facts) },
    ],
  },
];

/** Shown on the review step. */
export function summary(a, court) {
  const rows = [];
  const add = (l, v) => v && rows.push([l, v]);
  add("Court", court.name);
  add("Case", isUD(a) ? "Unlawful detainer" : a.ctype ? "Civil case" : "");
  add("Classification", CLASS_TXT[a.cls]);
  add("Claim", KIND_TXT[a.kind]);
  add("Complaint verified", a.verified === "yes" ? "Yes" : a.verified === "no" ? "No" : "");
  if (a.complex) add("Complex", a.complex === "yes" ? "Yes" : "No");
  add("Answer prepared", { general: "General denial (PLD-050)", form: "Judicial Council answer form", paper: "Pleading paper" }[a.pleading]);
  if (a.xc) add("Cross-complaint", a.xc === "yes" ? "Yes" : "No");
  add("Service complete", fmtISO(a.served));
  if (a.ext) add("Extension", { none: "None", stip: "15-day stipulation", order: `Court order, to ${fmtISO(a.extDate)}` }[a.ext]);
  return rows;
}

/* ------------------------------------------------------------------ checklist */
export const rulebook = {
  steps: STEPS,
  facts,
  sections: [
    { id: "prep", title: "Prepare the answer", phases: ["prepare"] },
    { id: "fees", title: "Fees", phases: ["fees"] },
    { id: "file", title: "File and serve", phases: ["file", "serve"] },
    { id: "time", title: "If more time is needed", phases: ["deadlines"] },
    { id: "after", title: "After filing", phases: ["after"] },
  ],
  // The first paper fee, with the court's local surcharge where it has one.
  vars(a, court) {
    const fee = firstPaperFee(court, a.cls, { filer: "defendant" });
    return {
      fee: {
        amount: money(fee.amount),
        label: `${(CLASS_TXT[a.cls] || "").toLowerCase()}${isUD(a) ? ", unlawful detainer" : ""}`,
        localCite: fee.local ? `; ${fee.local.cite}` : "",
        note: fee.local ? fee.local.text : "The statewide amount. Riverside, San Bernardino and San Francisco add a local surcharge.",
      },
    };
  },
  flags(c, a, court, { due }) {
    if (due.date < parse(todayISO())) c.flag("bad", "The due date has passed.", `The answer was due ${fmt(due.date)}. Check the docket for a request for entry of default before filing.`);
    else c.flag("ok", "Answer due", `${fmt(due.date)} (${due.how}).`);
  },
  extras(c, a, court, { due }) {
    const fee = firstPaperFee(court, a.cls, { filer: "defendant" });
    c.row("Answer due", fmt(due.date), `${due.how} (${due.cite})`);
    c.row("First paper fee", money(fee.amount) + (a.complex === "yes" ? " plus $1,000 complex fee" : ""), fee.local ? `Fee schedule of ${court.name}` : "Statewide rate");
    c.row("Form", { general: "General Denial (PLD-050)", form: formFor(a) || "", paper: "Pleading paper" }[a.pleading] || "");
    if (a.served) c.dates(court, parse(a.served), due.date);
  },
  verify: [
    { when: "xc = yes and not limited", text: "The downloaded fee schedule lists no separate fee for a cross-complaint filed with the answer. Check with the clerk of {court.name}." },
    { when: "xc = yes and limited", text: "The downloaded fee schedule lists no separate fee for a cross-complaint filed with the answer, other than the fees for one that raises the amount at issue or reclassifies the case. Check with the clerk of {court.name}." },
    { when: "ud", text: "Unlawful detainer answers can have their own timing and content rules beyond CCP 1167, such as for tenants protected by local ordinances. Those are not covered." },
    { when: "not ud", text: "CRC 3.110 (the 15-day stipulation and the default timing) does not apply to collections cases under CRC 3.740(a). The rules for those cases are not covered here." },
    { when: "economicLitigation", text: "If the case was withdrawn from the economic litigation rules on motion (CCP 91(c)), CCP 92(b) and the limited civil case exception in CCP 431.30(d) don't apply, so a verified complaint needs a verified answer with positive denials. The checklist assumes the case wasn't withdrawn." },
    { text: "Demurrers, motions to strike and other responses instead of an answer are not covered here." },
    { text: "The fee amounts are from the Statewide Civil Fee Schedule effective January 1, 2026." },
  ],
};

export const build = (a, court) => buildChecklist({ rulebook, caseFacts }, a, court);

// Checklist items were keyed "<section>.<key>" before the steps file (#223); a tick saved under an old key moves to
// the steps that took its place.
export const renamedChecks = {
  "prep.gd": ["general-denial-unverified", "general-denial-limited"],
  "prep.gddef": ["general-denial-defenses"],
  "prep.form": ["council-form-pi", "council-form-contract", "council-form-ud"],
  "prep.format": ["pleading-paper"],
  "prep.deny": ["positive-denials", "denials"],
  "prep.defenses": ["defenses"],
  "prep.noaffirm": ["no-affirmative-relief"],
  "prep.verify": ["verify"],
  "prep.sign": ["sign"],
  "prep.xc": ["cross-complaint"],
  "prep.xcserve": ["cross-complaint-service"],
  "prep.xcadr": ["cross-complaint-adr"],
  "prep.counter": ["counter-cover-sheet"],
  "fees.first": ["first-paper-fee-unlimited", "first-paper-fee-limited-over-10k", "first-paper-fee-limited-up-to-10k"],
  "fees.xcamt": ["cross-complaint-amount-fee"],
  "fees.xcreclass": ["reclassification-fee"],
  "fees.complex": ["complex-fee"],
  "file.efile": ["file"],
  "file.firstefile": ["first-efiling"],
  "file.serve": ["serve"],
  "file.due": ["due-unlawful-detainer", "due-order", "due-stipulation", "due"],
  "file.uddept": ["unlawful-detainer-department"],
  "time.stip": ["stipulated-extension"],
  "time.order": ["court-extension"],
  "time.local": ["local-extension"],
  "time.default": ["default"],
  "after.cms": ["case-management"],
  "after.jury": ["jury-fee"],
  "after.demur": ["demurrer-to-answer"],
  "after.xcresp": ["cross-complaint-response"],
  "after.udjury": ["jury-fee-unlawful-detainer"],
  "after.protem": ["judge-pro-tem"],
};
